import format from 'date-fns/format';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import {
  DATE_PICKER_FIELD_DISPLAY_FORMAT,
  DATE_PICKER_FIELD_VALUE_FORMAT,
  formatDateValue,
  parseDateValue,
  toDisabledDateKey,
} from '@shared/components/inputs/DatePickerField/utils/dateValueFormat';
import type { DatePickerFieldProps } from '@shared/components/inputs/DatePickerField/DatePickerField.types';

const DatePickerField = ({
  value,
  onChange,
  onBlur,
  name,
  ariaLabel,
  ariaDescribedBy,
  minDate,
  maxDate,
  disabledDates,
  error,
  helperText,
  fullWidth = true,
  placeholder = 'MM/DD/YYYY',
  disabled,
  isClearable = false,
  sx,
}: DatePickerFieldProps) => {
  const disabledKeys = new Set((disabledDates ?? []).map(toDisabledDateKey).filter(Boolean));

  const shouldDisableDate = disabledKeys.size
    ? (day: Date) => disabledKeys.has(format(day, DATE_PICKER_FIELD_VALUE_FORMAT))
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={parseDateValue(value)}
        onChange={(date) => onChange(formatDateValue(date))}
        minDate={minDate}
        maxDate={maxDate}
        format={DATE_PICKER_FIELD_DISPLAY_FORMAT}
        disabled={disabled}
        shouldDisableDate={shouldDisableDate}
        slotProps={{
          field: { clearable: isClearable, onClear: () => onChange('') },
          textField: {
            fullWidth,
            name,
            placeholder,
            onBlur,
            sx,
            error,
            helperText: helperText ?? '',
            FormHelperTextProps: { component: FormErrorMessage },
            inputProps: {
              'aria-label': ariaLabel,
              'aria-describedby': ariaDescribedBy,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DatePickerField;
